import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';
import './MediaForm.css'; // Reuse the same form styles
import api from '../services/api';
import { showToast } from '../components/ToastNotification';

/**
 * Normalize a date value from the API to YYYY-MM-DD format for <input type="date">.
 * Handles: "2025-01-15", "2025-01-15 00:00:00", ISO datetime strings, etc.
 */
function normalizeDate(val) {
  if (!val) return '';
  // If already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  // Try to parse and extract date part
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function ProjectForm({ openTab, closeTab, activeTab, mediaId, mediaName, projectId }) {
  const isEditMode = !!projectId;

  // Form state
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [accountingDate, setAccountingDate] = useState('');
  const [gdriveLink, setGdriveLink] = useState('');
  const [accountingCode, setAccountingCode] = useState('');
  const [pages, setPages] = useState('0.0');

  // Form submission state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Load existing project data in edit mode
  useEffect(() => {
    if (!isEditMode) return;

    const fetchProject = async () => {
      setLoadingData(true);
      try {
        const result = await api.getProject(projectId);
        const data = result.data;
        if (data) {
          setName(data.name || '');
          setDate(normalizeDate(data.date));
          setAccountingDate(normalizeDate(data.accounting_date));
          // Extract GDrive link from note field (backward compatible)
          const noteVal = data.note || '';
          const urlMatch = noteVal.match(/https?:\/\/[^\s]+/);
          setGdriveLink(urlMatch ? urlMatch[0] : '');
          setAccountingCode(data.accounting_code || '');
          setPages(data.pages || '0.0');
          setInitialLoaded(true);
        }
      } catch (err) {
        setError('Nepodařilo se načíst data projektu.');
        console.error('Failed to load project:', err);
      } finally {
        setLoadingData(false);
      }
    };

    fetchProject();
  }, [projectId, isEditMode]);

  // Track form changes (only after initial load in edit mode)
  useEffect(() => {
    if (isEditMode && !initialLoaded) return;
    if (name || date || accountingDate || gdriveLink || accountingCode || pages !== '0.0') {
      setIsDirty(true);
    }
  }, [name, date, accountingDate, gdriveLink, accountingCode, pages, isEditMode, initialLoaded]);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      const confirmed = window.confirm('Máte neuložené změny. Opravdu chcete zavřít formulář?');
      if (!confirmed) return;
    }
    if (closeTab && activeTab) {
      closeTab(activeTab);
    }
  }, [isDirty, closeTab, activeTab]);

  const handleSave = async () => {
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Název projektu je povinný.');
      return;
    }
    if (!date) {
      setError('Datum vydání je povinné.');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        medium_id: mediaId,
        date,
        accounting_date: accountingDate || null,
        note: gdriveLink.trim() || null,
        accounting_code: accountingCode.trim() || null,
        pages: pages || '0.0',
      };

      if (isEditMode) {
        await api.updateProject(projectId, data);

        // Close this edit tab
        if (closeTab && activeTab) {
          closeTab(activeTab);
        }
        // Return to media detail
        openTab(
          `media-detail-${mediaId}`,
          mediaName || 'Médium',
          `/media-pricelist/${mediaId}`,
          'MediaPricelistDetail',
          { mediaId: mediaId }
        );
        showToast('Projekt byl úspěšně upraven.', 'success');
        // Trigger refresh of media detail
        window.dispatchEvent(new CustomEvent('media:refresh', { detail: { mediaId } }));
      } else {
        const result = await api.createProject(data);
        const newId = result.data?.id;

        // Close this form tab
        if (closeTab && activeTab) {
          closeTab(activeTab);
        }
        // Return to media detail
        openTab(
          `media-detail-${mediaId}`,
          mediaName || 'Médium',
          `/media-pricelist/${mediaId}`,
          'MediaPricelistDetail',
          { mediaId: mediaId }
        );
        showToast('Projekt byl úspěšně vytvořen.', 'success');
        // Trigger refresh of media detail
        window.dispatchEvent(new CustomEvent('media:refresh', { detail: { mediaId } }));
      }
    } catch (err) {
      setError(err.message || `Nepodařilo se ${isEditMode ? 'uložit změny' : 'vytvořit projekt'}.`);
    } finally {
      setSaving(false);
    }
  };

  const handleGoToMediaDetail = () => {
    openTab(
      `media-detail-${mediaId}`,
      mediaName || 'Médium',
      `/media-pricelist/${mediaId}`,
      'MediaPricelistDetail',
      { mediaId: mediaId }
    );
  };

  if (loadingData) {
    return (
      <Container fluid className="media-form-container">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Načítání dat…</div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="media-form-container">

      {/* Breadcrumb */}
      <div className="breadcrumb-custom">
        <a onClick={() => openTab('media-pricelist', 'Média a ceníky', '/media-pricelist', 'MediaPricelist')}>
          Média a ceníky
        </a>
        <i className="bi bi-chevron-right" style={{ color: '#6c757d', fontSize: '.85rem' }}></i>
        <a onClick={handleGoToMediaDetail}>
          {mediaName || 'Médium'}
        </a>
        <i className="bi bi-chevron-right" style={{ color: '#6c757d', fontSize: '.85rem' }}></i>
        <span className="fw-semibold">{isEditMode ? 'Upravit projekt' : 'Nový projekt'}</span>
      </div>

      {/* Error alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
          {error}
        </Alert>
      )}

      {/* Form Card */}
      <div className="media-form-card">
        <div className="card-header-custom">
          <h5 className="mb-0">{isEditMode ? 'Upravit projekt' : 'Nový projekt'}</h5>
        </div>

        <div className="card-body-custom">

          {/* Základní informace */}
          <div className="form-section">
            <div className="section-title">Základní informace</div>
            <div className="row g-3">
              <div className="col-md-12">
                <label className="form-label">Název projektu <span className="text-danger">*</span></label>
                <Form.Control
                  type="text"
                  placeholder="např. Vydání č. 12/2025"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Datum vydání <span className="text-danger">*</span></label>
                <Form.Control
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Účetní datum</label>
                <Form.Control
                  type="date"
                  value={accountingDate}
                  onChange={(e) => setAccountingDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Doplňkové informace */}
          <div className="form-section">
            <div className="section-title">Doplňkové informace</div>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Google Drive link</label>
                <Form.Control
                  type="url"
                  placeholder="https://drive.google.com/..."
                  value={gdriveLink}
                  onChange={(e) => setGdriveLink(e.target.value)}
                />
                <div className="form-hint">Odkaz na složku nebo soubor na Google Drive</div>
              </div>
              <div className="col-md-4">
                <label className="form-label">Účetní kód</label>
                <Form.Control
                  type="text"
                  placeholder="např. PRJ-2025-012"
                  value={accountingCode}
                  onChange={(e) => setAccountingCode(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Stránky</label>
                <Form.Control
                  type="text"
                  placeholder="0.0"
                  value={pages}
                  onChange={(e) => setPages(e.target.value)}
                />
                <div className="form-hint">Počet stránek (výchozí 0.0)</div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="form-footer">
        <div className="form-footer-hint">
          <span className="text-danger">*</span> Povinná pole
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={handleCancel}>
            Zrušit
          </Button>
          <Button variant="primary" className="px-4" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Ukládám…
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-1"></i>
                {isEditMode ? 'Uložit změny' : 'Uložit projekt'}
              </>
            )}
          </Button>
        </div>
      </div>

    </Container>
  );
}

export default ProjectForm;
